import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { sanitizeRichtextHtml } from '../../sanitize/sanitizeRichtextHtml.js';
import { marginFields, visibilityField } from '../_shared/blockFields.js';
import { marginStyle, num, str, visibilityCss, richtextProseCss } from '../_shared/blockRender.js';
import { useProductState } from '../_shared/productContext.js';
/**
 * Shrine `tabs` (product buy-box; presets "Content tabs" / "Information display") — config-by-config.
 *
 * Section shape: a row of tab HEADERS (title + optional leading icon) sitting above a single shared
 * BODY panel that shows the active tab's richtext. Three repeatable tab entries (Shrine fixes the count
 * at 3 — tab_1/2/3 — so we mirror that with three tab groups). The first tab is active on SSR; clicking
 * a header swaps the body client-side. Without JS the first tab's body is the server-rendered default and
 * stays visible (graceful degradation).
 *
 * SHRINE INVENTORY (33 settings — every non-Shopify-only one implemented + wired):
 *  Layout: tab_text_size, tab_text_size_mobile, tab_text_icon_scale, button_alignment (left/center),
 *          divider_thickness, active_indicator_thickness, content_size.
 *  Colors: tab_text_color, active_tab_color, divider_color, active_indicator_color, content_text_color.
 *  Tab #1/#2/#3: title (text), custom_icon (image), content (richtext) — each wired.
 *  Margins: margin_top, margin_bottom.
 *  Visibility: the All-devices / Mobile-only / Desktop-only select.
 *
 * The `[description]` Liquid token (supported by Shrine's tab_*_content) is honoured: a tab body of exactly
 * `[description]` (or containing it) is substituted with the live `product.description` before render — the
 * closest standalone equivalent of Shopify's server-side token.
 *
 * DEFERRED (justified — render the control where one exists; real plumbing lives elsewhere):
 *  - tab_*_icon + tab_*_filled_icon (Shopify GLYPH-FONT: Material-Symbols icon-font name + fill axis) —
 *    requires the Google Material-Symbols glyph font, a separate sub-project. We KEEP the per-tab
 *    `customIcon` image picker (Shrine's own alternative leading-icon control) so tabs can still carry an
 *    icon; the glyph-font name/fill toggle are dropped (no font to drive them).
 *  - tab_*_page (Shopify LIQUID page-object: binds a Shopify Page's `.content` into the tab body) — a
 *    Shopify storefront resource with no standalone equivalent; the tab's own `content` richtext is the
 *    source of truth instead.
 *  (tab_*_custom_icon's Shopify-CDN `image_url`/srcset filter is platform image plumbing; we implement the
 *   picker + render the chosen URL directly. No animation / app / cart-AJAX settings exist on this block.)
 */
const SCHEME = { ref: 'custom' };
const color = (value) => ({ ...SCHEME, value });
function tabGroup(n, title, contentHtml, titleKey, contentKey) {
    const group = `Tab #${n}`;
    return [
        {
            id: `tab${n}Title`,
            type: 'text',
            label: 'Title',
            group,
            default: title,
            ...(titleKey ? { defaultKey: titleKey } : {}),
            maxLength: 60,
        },
        {
            id: `tab${n}CustomIcon`,
            type: 'image',
            label: 'Custom icon',
            group,
            info: 'Optional leading icon image, shown before the tab title.',
        },
        {
            id: `tab${n}Content`,
            type: 'richtext',
            label: 'Content',
            group,
            info: 'Tab body. Use [description] to insert the live product description.',
            default: { format: 'html', html: contentHtml },
            ...(contentKey ? { defaultKey: contentKey } : {}),
        },
    ];
}
export const tabsBlockSchema = {
    type: 'tabs',
    name: 'Tabs',
    max: 1,
    fields: [
        visibilityField,
        // ── Layout ──────────────────────────────────────────────────────────────────
        {
            id: 'tabTextSize',
            type: 'range',
            label: 'Tab text size (desktop)',
            group: 'Layout',
            min: 8,
            max: 24,
            step: 1,
            default: 16,
            unit: 'px',
        },
        {
            id: 'tabTextSizeMobile',
            type: 'range',
            label: 'Tab text size (mobile)',
            group: 'Layout',
            min: 8,
            max: 20,
            step: 1,
            default: 14,
            unit: 'px',
        },
        {
            id: 'tabTextIconScale',
            type: 'range',
            label: 'Icon scale',
            group: 'Layout',
            info: 'Leading icon size relative to the tab text.',
            min: 100,
            max: 200,
            step: 10,
            default: 130,
            unit: '%',
        },
        {
            id: 'buttonAlignment',
            type: 'select',
            label: 'Tab alignment',
            group: 'Layout',
            default: 'center',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
            ],
        },
        {
            id: 'dividerThickness',
            type: 'range',
            label: 'Divider thickness',
            group: 'Layout',
            min: 1,
            max: 3,
            step: 1,
            default: 2,
            unit: 'px',
        },
        {
            id: 'activeIndicatorThickness',
            type: 'range',
            label: 'Active indicator thickness',
            group: 'Layout',
            min: 1,
            max: 3,
            step: 1,
            default: 2,
            unit: 'px',
        },
        {
            id: 'contentSize',
            type: 'range',
            label: 'Content text size',
            group: 'Layout',
            min: 8,
            max: 20,
            step: 1,
            default: 12,
            unit: 'px',
        },
        // ── Colors ──────────────────────────────────────────────────────────────────
        {
            id: 'tabTextColor',
            type: 'color',
            label: 'Tab text',
            group: 'Colors',
            default: color('#595959'),
        },
        {
            id: 'activeTabColor',
            type: 'color',
            label: 'Active tab text',
            group: 'Colors',
            default: color('#121212'),
        },
        {
            id: 'dividerColor',
            type: 'color',
            label: 'Divider',
            group: 'Colors',
            default: color('#E5E5E5'),
        },
        {
            id: 'activeIndicatorColor',
            type: 'color',
            label: 'Active indicator',
            group: 'Colors',
            default: color('#121212'),
        },
        {
            id: 'contentTextColor',
            type: 'color',
            label: 'Content text',
            group: 'Colors',
            default: color('#121212'),
        },
        // ── Tab #1 / #2 / #3 ─────────────────────────────────────────────────────────
        ...tabGroup(1, 'Description', '<p>Décrivez votre produit</p>', 'product.details'),
        ...tabGroup(2, 'Livraison', '<p>Informations de livraison</p>', 'cart.shipping', 'product.shipping_info'),
        ...tabGroup(3, 'Matière', '<p>Informations sur la matière</p>', 'section.tab_material', 'section.tab_material_text'),
        // ── Margins ──────────────────────────────────────────────────────────────────
        ...marginFields(15, 15),
    ],
};
/** A stored `color` setting is a ColorValue; read its hex (custom) or fall back to a literal. */
function colorHex(v, fallback) {
    if (v && typeof v === 'object') {
        const c = v;
        if (c.ref === 'custom' && typeof c.value === 'string')
            return c.value;
    }
    if (typeof v === 'string' && v.length > 0)
        return v;
    return fallback;
}
/** Resolve a `customIcon` image setting to its URL (or '' when unset). */
function imageUrl(v) {
    if (v && typeof v === 'object') {
        const img = v;
        if (typeof img.url === 'string')
            return img.url;
        if (typeof img.src === 'string')
            return img.src;
    }
    return '';
}
/**
 * Build the (up to 3) tab entries from the block settings. A tab is included when it has either a title
 * or content (so an emptied-out tab disappears, matching Shrine's "blank tab is skipped" behaviour). The
 * `[description]` token in a tab body is replaced with the live product description.
 */
function buildTabs(bs, description) {
    const out = [];
    for (const n of [1, 2, 3]) {
        const title = str(bs[`tab${n}Title`]);
        const rt = bs[`tab${n}Content`];
        const rawHtml = rt?.format === 'html' ? rt.html : '';
        if (!title && !rawHtml)
            continue;
        // Honour the [description] Liquid token (case-insensitive), substituting the live description.
        const tokened = rawHtml.replace(/\[description\]/gi, description ?? '');
        out.push({
            title: title || `Tab ${n}`,
            iconUrl: imageUrl(bs[`tab${n}CustomIcon`]),
            html: sanitizeRichtextHtml(tokened),
        });
    }
    return out;
}
/**
 * A tabbed content panel: a header row + the active tab's body. First tab active on SSR (`useState(0)`);
 * clicking a header swaps the active body client-side. The header row sits on a divider line; the active
 * header carries the coloured under-indicator. Honours tab text size (desktop/mobile), icon scale,
 * alignment (left/center), divider + indicator thickness, content size, and the five colours.
 */
const TabsBlock = ({ block }) => {
    const ps = useProductState();
    if (!ps)
        return null;
    const bs = block.settings;
    const cls = `aqx-tab-${block.id}`;
    const tabs = buildTabs(bs, ps.product.description ?? '');
    // Hooks must run unconditionally; default the active tab to the first.
    const [active, setActive] = useState(0);
    if (tabs.length === 0)
        return null;
    const activeIndex = active < tabs.length ? active : 0;
    const textSize = num(bs.tabTextSize, 16);
    const textSizeMobile = num(bs.tabTextSizeMobile, 14);
    const iconScale = num(bs.tabTextIconScale, 130) / 100;
    const alignLeft = str(bs.buttonAlignment, 'center') === 'left';
    const dividerThickness = num(bs.dividerThickness, 2);
    const indicatorThickness = num(bs.activeIndicatorThickness, 2);
    const contentSize = num(bs.contentSize, 12);
    const tabTextColor = colorHex(bs.tabTextColor, '#595959');
    const activeTabColor = colorHex(bs.activeTabColor, '#121212');
    const dividerColor = colorHex(bs.dividerColor, '#E5E5E5');
    const indicatorColor = colorHex(bs.activeIndicatorColor, '#121212');
    const contentTextColor = colorHex(bs.contentTextColor, '#121212');
    const iconPx = Math.round(textSize * iconScale);
    // Header row: flex row on the divider line; alignment left/center; each header a flat button whose
    // ::after is the active under-indicator. Desktop sizing via @media + @container; mobile is the base.
    const css = `.${cls}__row{display:flex;flex-wrap:wrap;gap:24px;justify-content:${alignLeft ? 'flex-start' : 'center'};` +
        `border-bottom:${dividerThickness}px solid ${dividerColor};margin:0}` +
        `.${cls}__tab{appearance:none;background:transparent;border:none;cursor:pointer;padding:10px 0;` +
        `display:inline-flex;align-items:center;gap:8px;font-family:var(--aqx-theme-heading-font,var(--aqx-theme-font));` +
        `font-weight:600;font-size:${textSizeMobile}px;line-height:1.2;color:${tabTextColor};` +
        `position:relative;margin-bottom:-${dividerThickness}px;transition:color .15s ease}` +
        `.${cls}__tab[aria-selected="true"]{color:${activeTabColor}}` +
        `.${cls}__tab[aria-selected="true"]::after{content:"";position:absolute;left:0;right:0;bottom:-${dividerThickness}px;` +
        `height:${indicatorThickness}px;background:${indicatorColor}}` +
        `.${cls}__icon{display:inline-flex;width:${iconPx}px;height:${iconPx}px}` +
        `.${cls}__icon img{width:100%;height:100%;object-fit:contain;display:block}` +
        `.${cls}__body{padding-top:14px;font-size:${contentSize}px;line-height:var(--aqx-theme-lh-relaxed,1.7);` +
        `color:${contentTextColor}}` +
        `.${cls}__body p{margin:0 0 8px}.${cls}__body p:last-child{margin-bottom:0}` +
        // Restore list markers / heading sizes / underline in the tab richtext body (Preflight strips them).
        richtextProseCss(`${cls}__body`) +
        // Desktop tab size via `@container aqx-page` ONLY (always present via PageRenderer) — a paired
        // @media(min-width) would fire off the builder's wide editor window inside a narrow mobile preview.
        `@container aqx-page (min-width:750px){.${cls}__tab{font-size:${textSize}px}}` +
        visibilityCss(cls, bs.visibility);
    const wrap = {
        fontFamily: 'var(--aqx-theme-font)',
        wordBreak: 'break-word',
        ...marginStyle(bs, 15, 15),
    };
    return (_jsxs("div", { className: cls, style: wrap, children: [_jsx("style", { dangerouslySetInnerHTML: { __html: css } }), _jsx("div", { className: `${cls}__row`, role: "tablist", children: tabs.map((t, i) => {
                    const selected = i === activeIndex;
                    const icon = t.iconUrl ? (_jsx("span", { className: `${cls}__icon`, "aria-hidden": "true", children: _jsx("img", { src: t.iconUrl, alt: "" }) })) : null;
                    return (_jsxs("button", { type: "button", role: "tab", id: `${cls}-tab-${i}`, "aria-selected": selected, "aria-controls": `${cls}-panel-${i}`, className: `${cls}__tab`, onClick: () => setActive(i), children: [icon, _jsx("span", { children: t.title })] }, i));
                }) }), tabs.map((t, i) => (_jsx("div", { role: "tabpanel", id: `${cls}-panel-${i}`, "aria-labelledby": `${cls}-tab-${i}`, className: `${cls}__body`, hidden: i !== activeIndex, dangerouslySetInnerHTML: { __html: t.html } }, i)))] }));
};
export const tabsBlock = {
    type: 'tabs',
    schema: tabsBlockSchema,
    Component: TabsBlock,
    meta: { label: 'Tabs' },
};
//# sourceMappingURL=index.js.map